import { readinessStatusLabels, type ReadinessStatus } from '../types';
import '../employee-readiness.css';

/** What a chip can show: a server-computed status, or one of three non-result states so a failed or
 * pending read is never rendered as if it were a (reassuring) result. */
export type ReadinessChipValue = ReadinessStatus | 'loading' | 'unavailable' | 'forbidden';

const GLYPHS: Record<ReadinessChipValue, string> = {
  Ready: '✓',
  ReadyWithWarnings: '!',
  Blocked: '✕',
  NeedsReview: '?',
  NotApplicable: '–',
  loading: '…',
  unavailable: '–',
  forbidden: '–',
};

const MODIFIERS: Record<ReadinessChipValue, string> = {
  Ready: 'ready',
  ReadyWithWarnings: 'warnings',
  Blocked: 'blocked',
  NeedsReview: 'review',
  NotApplicable: 'na',
  loading: 'muted',
  unavailable: 'muted',
  forbidden: 'muted',
};

function labelFor(value: ReadinessChipValue): string {
  switch (value) {
    case 'loading': return 'Checking…';
    case 'unavailable': return 'Unavailable';
    case 'forbidden': return 'No access';
    default: return readinessStatusLabels[value];
  }
}

function titleFor(value: ReadinessChipValue): string {
  switch (value) {
    case 'loading': return 'Checking payroll readiness';
    case 'unavailable': return 'Payroll readiness could not be loaded right now.';
    case 'forbidden': return 'You do not have permission to view payroll readiness.';
    default: return `Payroll readiness: ${readinessStatusLabels[value]}`;
  }
}

/** Colour is never the only signal: every chip carries a text label and a glyph. */
export function ReadinessStatusChip({ value }: { value: ReadinessChipValue }) {
  return (
    <span className={`readiness-chip readiness-chip--${MODIFIERS[value]}`} data-readiness={value} title={titleFor(value)}>
      <span aria-hidden="true" className="readiness-chip-glyph">{GLYPHS[value]}</span>
      {labelFor(value)}
    </span>
  );
}
